import clsx from 'clsx';
import { type MouseEvent as ReactMouseEvent } from 'react';
import type {
  OptionProps as ReactSelectOptionProps,
  GroupBase,
} from 'react-select';

import { Flex } from '@dynatrace/strato-components/layouts';
import { CheckmarkSmallIcon } from '@dynatrace/strato-icons';

import { TextEllipsisAroundChildren } from '../../../core/components/TextEllipsisChildren.js';
import { useSelectMenuContext } from '../hooks/use-select-menu-context.js';
import { SelectActions } from '../state/select-menu-state-reducer.js';
import {
  selectOptionMultiCSS,
  selectOptionMultiIndicatorCSS,
  selectOptionMultiIndicatorIconCSS,
  selectOptionPrefixCSS,
  selectOptionSingleCSS,
  selectOptionSuffixCSS,
} from '../styles/Select.sty.js';
import type { BaseOption, BaseGroup } from '../types.js';

export type OptionProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectOptionProps<OptionValue, isMulti, Group>;

export function Option<
  OptionValue,
  isMulti extends boolean,
  Group extends BaseGroup<OptionValue>,
>(props: Readonly<OptionProps<BaseOption<OptionValue>, isMulti, Group>>) {
  const {
    children,
    isSelected,
    innerProps,
    isFocused,
    isDisabled,
    isMulti,
    selectProps,
  } = props;
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    prefix,
    suffix,
  } = props.data;

  const { dispatch } = useSelectMenuContext();

  return (
    <Flex
      data-testid={dataTestId}
      role="option"
      {...innerProps}
      className={clsx(
        'strato-selectV2-option',
        isMulti === true ? selectOptionMultiCSS : selectOptionSingleCSS,
        {
          'strato-selectV2-option--selected': isSelected,
          'strato-selectV2-option--focus': selectProps.focusOnOption
            ? isFocused
            : false,
          'strato-selectV2-option--disabled': isDisabled,
        },
        consumerClassName,
      )}
      style={consumerStyle}
      onClick={(event: ReactMouseEvent<HTMLDivElement>) => {
        if (!isDisabled) {
          // Only if the option is in single select mode, we can close the menu.
          if (isMulti === false) {
            dispatch({
              type: SelectActions.CLOSE,
            });
          }
          innerProps.onClick?.(event);
        }
      }}
      aria-selected={isSelected}
    >
      {isMulti === true ? (
        <div
          className={clsx(
            selectOptionMultiIndicatorCSS({
              selected: isSelected,
            }),
          )}
        >
          {isSelected === true ? (
            <CheckmarkSmallIcon className={selectOptionMultiIndicatorIconCSS} />
          ) : null}
        </div>
      ) : null}
      {/* Check if any of the options have a prefix defined. If they do, all other items will need to respect the space */}
      {props.selectProps.optionsHavePrefix ? (
        <span
          className={clsx(
            selectOptionPrefixCSS,
            prefix.componentProps?.className,
          )}
          style={prefix.componentProps?.style}
          data-testid={prefix.componentProps?.['data-testid']}
        >
          {prefix.component}
        </span>
      ) : null}
      <TextEllipsisAroundChildren>{children}</TextEllipsisAroundChildren>
      {/* For the Suffix icon, we will not need to deal with the other options. Overlaps are fine here. */}
      {suffix.component ? (
        <span
          className={clsx(
            selectOptionSuffixCSS,
            suffix.componentProps?.className,
          )}
          style={suffix.componentProps?.style}
          data-testid={suffix.componentProps?.['data-testid']}
        >
          {suffix.component}
        </span>
      ) : null}
    </Flex>
  );
}
